import { normalizeExportPath, titleFromExportFilename } from './parse-filename.js';
import type { NotionExportNode } from './types.js';

/**
 * A page/database is represented on disk by a sidecar `.md` (and, for a
 * database, `.csv`/`_all.csv`) file whose name carries the full 32-char Notion
 * id. Its children live in a *sibling folder* named after the title. Newer
 * Notion exports truncate that folder name — dropping the id entirely, or using
 * a `{first4}-{last4}` short form when two siblings share a title — so the
 * folder and its sidecar can only be matched by title, not by id.
 */
type Sidecar = {
  title: string;
  notionId: string | null;
  hexRaw: string | null;
  mdPath: string;
  csvPath: string;
  hasCsv: boolean;
};

function formatUuid(hex: string): string {
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function basename(path: string): string {
  return path.split('/').pop() ?? path;
}

function sameTitle(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function pushInto<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) {
    list.push(value);
  } else {
    map.set(key, [value]);
  }
}

function parseSidecarFilename(name: string): {
  title: string;
  notionId: string | null;
  hexRaw: string | null;
  isAll: boolean;
} {
  const isAll = /_all\.csv$/i.test(name);
  const base = name.replace(/\.(md|csv|html)$/i, '').replace(/_all$/i, '');
  const match = base.match(/([0-9a-f]{32})$/i);
  const hexRaw = match ? match[1]!.toLowerCase() : null;
  return {
    title: titleFromExportFilename(name),
    notionId: hexRaw ? formatUuid(hexRaw) : null,
    hexRaw,
    isAll,
  };
}

/**
 * A folder name is either the plain title, `Title {32hex}` (older exports), or
 * `Title {first4}-{last4}` (Notion's short disambiguation for same-titled
 * siblings). Extract the base title plus any id fragments for matching.
 */
function parseFolderName(name: string): {
  base: string;
  fullHex: string | null;
  prefix: string | null;
  suffix: string | null;
} {
  const full = name.match(/^(.*) ([0-9a-f]{32})$/i);
  if (full) {
    return { base: full[1]!, fullHex: full[2]!.toLowerCase(), prefix: null, suffix: null };
  }
  const short = name.match(/^(.*) ([0-9a-f]{4})-([0-9a-f]{4})$/i);
  if (short) {
    return { base: short[1]!, fullHex: null, prefix: short[2]!.toLowerCase(), suffix: short[3]!.toLowerCase() };
  }
  return { base: name, fullHex: null, prefix: null, suffix: null };
}

function makeNode(sidecar: Sidecar, children: NotionExportNode[]): NotionExportNode {
  return {
    notionId: sidecar.notionId,
    title: sidecar.title,
    // Database collection nodes have no page markdown of their own (matches the
    // index.html shape); their data comes from the CSV.
    href: sidecar.hasCsv ? '' : sidecar.mdPath,
    kind: sidecar.hasCsv ? 'database' : 'page',
    children,
  };
}

function buildSidecars(files: string[]): { byKey: Map<string, Sidecar>; order: string[] } {
  const byKey = new Map<string, Sidecar>();
  const order: string[] = [];

  for (const file of files) {
    const name = basename(file);
    const isMd = /\.md$/i.test(name);
    const isCsv = /\.csv$/i.test(name);
    if (!isMd && !isCsv) {
      continue;
    }
    const parsed = parseSidecarFilename(name);
    const key = parsed.hexRaw ? `hex:${parsed.hexRaw}` : `title:${parsed.title.toLowerCase()}`;

    let sidecar = byKey.get(key);
    if (!sidecar) {
      sidecar = {
        title: parsed.title,
        notionId: parsed.notionId,
        hexRaw: parsed.hexRaw,
        mdPath: '',
        csvPath: '',
        hasCsv: false,
      };
      byKey.set(key, sidecar);
      order.push(key);
    }
    if (parsed.notionId && !sidecar.notionId) {
      sidecar.notionId = parsed.notionId;
    }
    if (parsed.hexRaw && !sidecar.hexRaw) {
      sidecar.hexRaw = parsed.hexRaw;
    }

    if (isMd) {
      sidecar.mdPath = file;
      sidecar.title = parsed.title;
    } else {
      sidecar.hasCsv = true;
      if (!sidecar.csvPath || parsed.isAll) {
        sidecar.csvPath = file;
      }
      if (!sidecar.mdPath) {
        sidecar.title = parsed.title;
      }
    }
  }

  return { byKey, order };
}

/**
 * Match each child folder to its sidecar. Disambiguated/full-hex folders are
 * matched by id first (a same-title sibling may hold the plain folder name),
 * then remaining folders by exact title.
 */
function assignFolders(
  subdirs: string[],
  sidecars: { byKey: Map<string, Sidecar>; order: string[] },
): { assignment: Map<string, string>; usedKeys: Set<string> } {
  const assignment = new Map<string, string>();
  const usedKeys = new Set<string>();

  for (const subdir of subdirs) {
    const pf = parseFolderName(basename(subdir));
    if (!pf.fullHex && !pf.prefix) {
      continue;
    }
    for (const key of sidecars.order) {
      if (usedKeys.has(key)) {
        continue;
      }
      const sidecar = sidecars.byKey.get(key)!;
      const matches = pf.fullHex
        ? sidecar.hexRaw === pf.fullHex
        : Boolean(
            sidecar.hexRaw &&
              sidecar.hexRaw.startsWith(pf.prefix!) &&
              sidecar.hexRaw.endsWith(pf.suffix!) &&
              sameTitle(sidecar.title, pf.base),
          );
      if (matches) {
        assignment.set(subdir, key);
        usedKeys.add(key);
        break;
      }
    }
  }

  for (const subdir of subdirs) {
    if (assignment.has(subdir)) {
      continue;
    }
    const folderName = basename(subdir);
    for (const key of sidecars.order) {
      if (usedKeys.has(key)) {
        continue;
      }
      if (sameTitle(sidecars.byKey.get(key)!.title, folderName)) {
        assignment.set(subdir, key);
        usedKeys.add(key);
        break;
      }
    }
  }

  return { assignment, usedKeys };
}

function buildLevel(
  dir: string,
  filesByDir: Map<string, string[]>,
  childDirsByParent: Map<string, string[]>,
): NotionExportNode[] {
  const files = filesByDir.get(dir) ?? [];
  const subdirs = childDirsByParent.get(dir) ?? [];
  const sidecars = buildSidecars(files);
  const { assignment, usedKeys } = assignFolders(subdirs, sidecars);

  const nodes: NotionExportNode[] = [];

  for (const subdir of subdirs) {
    const children = buildLevel(subdir, filesByDir, childDirsByParent);
    const key = assignment.get(subdir);
    if (key) {
      nodes.push(makeNode(sidecars.byKey.get(key)!, children));
      continue;
    }
    // A folder with no sidecar (e.g. an asset-only or implicit container).
    // Keep it only if it holds importable content.
    if (children.length === 0) {
      continue;
    }
    const pf = parseFolderName(basename(subdir));
    nodes.push({
      notionId: pf.fullHex ? formatUuid(pf.fullHex) : null,
      title: titleFromExportFilename(pf.base),
      href: '',
      kind: 'page',
      children,
    });
  }

  for (const key of sidecars.order) {
    if (usedKeys.has(key)) {
      continue;
    }
    nodes.push(makeNode(sidecars.byKey.get(key)!, []));
  }

  return nodes;
}

/**
 * Reconstruct the Notion export node tree from the folder hierarchy alone, for
 * exports that no longer ship an `index.html`. Node kind is derived from CSV
 * presence, page hierarchy from folder nesting, and database rows from the
 * `.md` files inside a database's folder.
 */
export function buildTreeFromPaths(paths: Iterable<string>): NotionExportNode[] {
  const filesByDir = new Map<string, string[]>();
  const allDirs = new Set<string>();

  for (const rawPath of paths) {
    const norm = normalizeExportPath(rawPath);
    const segments = norm.split('/').filter(Boolean);
    if (segments.length === 0) {
      continue;
    }
    const dir = segments.slice(0, -1).join('/');
    pushInto(filesByDir, dir, norm);
    for (let i = 1; i < segments.length; i++) {
      allDirs.add(segments.slice(0, i).join('/'));
    }
  }

  const childDirsByParent = new Map<string, string[]>();
  for (const dir of allDirs) {
    const idx = dir.lastIndexOf('/');
    const parent = idx >= 0 ? dir.slice(0, idx) : '';
    pushInto(childDirsByParent, parent, dir);
  }

  return buildLevel('', filesByDir, childDirsByParent);
}
